import React, { useMemo, useRef, useState } from 'react';

import {
    TabsCarousel,
    CarouselHeader,
    CarouselTitle,
    CarouselTabs,
    CarouselTab,
    CarouselBody,
    CarouselPagination,
} from '@lux/carousel';
import { Translate, TranslateScope } from '@pegasus/translation';
import { getSessionStorageItem, setSessionStorageItem } from '@pegasus/utils';
import { ErrorBoundary } from '@browsing/components';
import { useNavigateToPDP } from '@browsing/hooks';
import { CatalogType, isModifierKeyPressed, NavigationSourceType } from '@browsing/utils';
import {
    FashionPreferencesGenderValues,
    setCrossCampaignFiltersCategoryId,
} from '@browsing/filters';
import { useContinueShopping } from './hooks/use-continue-shopping';
import { ContinueShoppingCardItem } from './types';
import { prepareContinueShoppingData, mapCardItemToTrackingItem } from './helpers';
import { CategoryPanel } from './components/category-panel';
import { useTracking } from './hooks/use-tracking';
import { useViewTracking } from './hooks/use-view-tracking';
import { CAROUSEL_BREAKPOINTS, SELECTED_TAB_STORAGE_KEY } from './constants';

export const ContinueShoppingCarouselInternal: React.FC = () => {
    const { trackArticleClick, trackSeeAllClick, trackTabChange, trackSwipe } = useTracking();
    const { continueShoppingData } = useContinueShopping();

    const { navigateToPDP, getPDPURLPath } = useNavigateToPDP();

    const { panelData } = useMemo(
        () => prepareContinueShoppingData(continueShoppingData),
        [continueShoppingData],
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const initialTabId = useMemo(() => {
        if (panelData.length === 0) {
            return '';
        }
        const savedTab = getSessionStorageItem(SELECTED_TAB_STORAGE_KEY);
        const validTabIds = panelData.map(item => item.category.categoryId.toString());
        if (savedTab && validTabIds.includes(savedTab)) {
            return savedTab;
        }

        return panelData[0].category.categoryId.toString();
    }, [panelData]);

    const [currentTabId, setCurrentTabId] = useState<string>(initialTabId);

    const currentTabTrackingItems = useMemo(() => {
        const currentPanel = panelData.find(
            panel => panel.category.categoryId.toString() === currentTabId,
        );
        if (!currentPanel) {
            return [];
        }
        return currentPanel.categoriesList.map(mapCardItemToTrackingItem);
    }, [panelData, currentTabId]);

    useViewTracking(containerRef, currentTabTrackingItems, currentTabId);

    if (panelData.length === 0) {
        return null;
    }

    const firstCategory = panelData[0].category;

    const getOnArticleClick =
        (item: ContinueShoppingCardItem, index: number) => (e: React.SyntheticEvent) => {
            setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, currentTabId);

            trackArticleClick(item, index);

            // Prevent clicks on LUX <ArticleCard/>, whose children are wrapped in a link, from navigating to the PDP.
            e.preventDefault();

            navigateToPDP({
                queryParams: {
                    campaignId: item.campaignIdentifier,
                    configSku: item.sku,
                    navigationSource: NavigationSourceType.ContinueShopping,
                    catalogType: CatalogType.Campaign,
                },
                shouldOpenInNewTab: isModifierKeyPressed(e),
            });
        };

    // Pass `pdpURLPath` to <ArticleCard/>'s link for accessibility/robustness, even though we prevent its
    // default behavior. e.g. the user might right-click the link, which is not handled here.
    const getArticleURLPath = (item: ContinueShoppingCardItem) => {
        return getPDPURLPath({
            queryParams: {
                campaignId: item.campaignIdentifier,
                configSku: item.sku,
                navigationSource: NavigationSourceType.ContinueShopping,
                catalogType: CatalogType.Campaign,
            },
        });
    };

    const handleSeeAllClick = (
        categoryId: number,
        selectedFashionPreference?: FashionPreferencesGenderValues | 'NO_PREFERENCE',
    ) => {
        setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, currentTabId);
        trackSeeAllClick();
        setCrossCampaignFiltersCategoryId(categoryId);
        if (selectedFashionPreference === 'MALE' || selectedFashionPreference === 'FEMALE') {
            setSessionStorageItem('selected_fashion_preference', selectedFashionPreference);
        }
    };

    const handleTabChange = (_params: { index: number; value: string }) => {
        if (_params.index < panelData.length) {
            const selectedCategory = panelData[_params.index]?.category;
            if (selectedCategory) {
                const tabId = selectedCategory.categoryId.toString();
                setCurrentTabId(tabId);
                setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, tabId);
                trackTabChange(tabId);
            }
        }
    };

    const handleSwipe = () => {
        trackSwipe();
    };

    const isSingleCategory = panelData.length === 1;
    const titleMessage = isSingleCategory ? 'singleCategoryHeader' : 'titleMultipleCategories';
    const titleParams =
        isSingleCategory && firstCategory.categoryName
            ? { category: firstCategory.categoryName }
            : undefined;

    return (
        <div ref={containerRef} data-testid="continue-shopping-carousel">
            <TranslateScope path="continueShopping">
                <TabsCarousel initialTab={initialTabId} colorMode="default" emphasisLevel="default">
                    <CarouselHeader>
                        <CarouselTitle>
                            <Translate message={titleMessage} params={titleParams} />
                        </CarouselTitle>

                        {panelData.length > 1 && (
                            <CarouselTabs onChange={handleTabChange}>
                                {panelData.map(({ category }) => (
                                    <CarouselTab
                                        key={category.categoryId}
                                        for={category.categoryId.toString()}
                                    >
                                        {category.categoryName}
                                    </CarouselTab>
                                ))}
                            </CarouselTabs>
                        )}
                    </CarouselHeader>

                    <CarouselBody
                        visibleItems={CAROUSEL_BREAKPOINTS.MOBILE}
                        visibleItemsSm={CAROUSEL_BREAKPOINTS.MOBILE_XS}
                        visibleItemsMd={CAROUSEL_BREAKPOINTS.TABLET_MD}
                        visibleItemsLg={CAROUSEL_BREAKPOINTS.DESKTOP_LG}
                        withOverflow
                        onScroll={handleSwipe}
                    >
                        {panelData.map(({ category, shouldShowSeeAll, categoriesList }) => (
                            <CategoryPanel
                                key={category.categoryId}
                                category={category}
                                categoriesList={categoriesList}
                                shouldShowSeeAll={shouldShowSeeAll}
                                getOnArticleClick={getOnArticleClick}
                                getArticleURLPath={getArticleURLPath}
                                onSeeAllClick={handleSeeAllClick}
                            />
                        ))}
                        <CarouselPagination />
                    </CarouselBody>
                </TabsCarousel>
            </TranslateScope>
        </div>
    );
};

export const ContinueShoppingCarousel: React.FC = () => (
    <ErrorBoundary componentName="ContinueShoppingCarousel">
        <ContinueShoppingCarouselInternal />
    </ErrorBoundary>
);
